/**
 * @fileoverview Generic Collection Yonetimi Hook'u
 *
 * Bu modul, compare ve save gibi koleksiyon islemlerini genellestirir.
 * Ayni pattern'i tekrarlamak yerine tek bir hook kullanilabilir.
 *
 * @module hooks/useCollection
 */

import { useState, useCallback, useMemo } from "react";

/**
 * @typedef {Object} UseCollectionOptions
 * @property {number} maxItems - Maksimum item sayisi
 * @property {Function} [idExtractor] - Item'dan ID cikaran fonksiyon (default: item => String(item.id))
 */

/**
 * @typedef {Object} UseCollectionReturn
 * @property {Array} items - Koleksiyon item'lari
 * @property {Set<string>} itemIdSet - Item ID'lerinin Set'i (hizli lookup icin)
 * @property {boolean} isAtLimit - Koleksiyon limite ulasti mi
 * @property {Function} toggle - Item ekle/cikar
 * @property {Function} add - Item ekle (limit kontrollu)
 * @property {Function} remove - Item cikar (ID ile)
 * @property {Function} clear - Koleksiyonu temizle
 * @property {Function} has - Item koleksiyonda mi kontrol et
 */

/**
 * Generic koleksiyon yonetimi hook'u.
 *
 * Bu hook, compare ve save gibi koleksiyon islemlerini genellestirir.
 * Toggle, add, remove ve clear islemlerini saglar.
 *
 * @function useCollection
 * @param {UseCollectionOptions} options - Hook secenekleri
 * @returns {UseCollectionReturn} Koleksiyon yonetim fonksiyonlari
 *
 * @example
 * // Compare kullanimi
 * const {
 *   items: compareListings,
 *   itemIdSet: compareListingIdSet,
 *   isAtLimit: isCompareAtLimit,
 *   toggle: toggleCompare,
 *   remove: removeFromCompare,
 *   clear: clearCompare,
 * } = useCollection({ maxItems: 3 });
 *
 * // Toggle kullanimi
 * toggleCompare(listing);
 *
 * // ID ile kontrol
 * const isInCompare = compareListingIdSet.has(String(listing.id));
 */
export function useCollection({
  maxItems,
  idExtractor = (item) => String(item.id),
}) {
  const [items, setItems] = useState([]);

  // ID Set (hizli lookup icin memoized)
  const itemIdSet = useMemo(
    () => new Set(items.map(idExtractor)),
    [items, idExtractor]
  );

  // Limit kontrolu
  const isAtLimit = items.length >= maxItems;

  /**
   * Item'in koleksiyonda olup olmadigini kontrol et.
   */
  const has = useCallback(
    (item) => {
      if (!item) return false;
      const id = idExtractor(item);
      return itemIdSet.has(id);
    },
    [idExtractor, itemIdSet]
  );

  /**
   * Item ekle/cikar (toggle).
   * Varsa cikarir, yoksa ekler (limit kontrollu).
   */
  const toggle = useCallback(
    (item) => {
      if (!item) return;
      const id = idExtractor(item);

      setItems((prev) => {
        const exists = prev.some((i) => idExtractor(i) === id);
        if (exists) {
          return prev.filter((i) => idExtractor(i) !== id);
        }
        if (prev.length < maxItems) {
          return [...prev, item];
        }
        return prev;
      });
    },
    [maxItems, idExtractor]
  );

  /**
   * Item ekle (limit kontrollu).
   * Zaten varsa veya limit doluysa ekleme yapmaz.
   */
  const add = useCallback(
    (item) => {
      if (!item) return;
      const id = idExtractor(item);

      setItems((prev) => {
        const exists = prev.some((i) => idExtractor(i) === id);
        if (exists || prev.length >= maxItems) {
          return prev;
        }
        return [...prev, item];
      });
    },
    [maxItems, idExtractor]
  );

  /**
   * Item cikar (ID ile).
   */
  const remove = useCallback(
    (id) => {
      if (id == null) return;
      const idStr = String(id);
      setItems((prev) => prev.filter((i) => idExtractor(i) !== idStr));
    },
    [idExtractor]
  );

  /**
   * Koleksiyonu temizle.
   */
  const clear = useCallback(() => {
    setItems([]);
  }, []);

  return {
    items,
    itemIdSet,
    isAtLimit,
    toggle,
    add,
    remove,
    clear,
    has,
  };
}

export default useCollection;
